import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import React, { useRef } from "react";

import { faExclamationCircle, faGripLines, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faXmarkCircle } from "@fortawesome/pro-solid-svg-icons";

//hooks
import {
	ArrayFieldState,
	ButtonStandard,
	FormField,
	FormInputText,
	FormList,
	Icon,
	InputNumeric,
	Text,
	useKeyPress,
} from "@performyard/py-components";

//components
import IconButton from "../../../../../../../components/Button/IconButton";
import DragAndDrop from "../../../../../../../components/DragAndDrop";
import Spacer from "../../../../../../../components/Spacer";

import { useCompanySettings } from "../../../../../../../api/hooks";

//utils
import { EditQuestionBlockProps } from "../../QuestionBlock";

export type QuestionOption = { value: number | ""; name: string; description: string };

type RatingScaleRowProps = {
	index: number;
	field: ArrayFieldState<QuestionOption>;
	fieldKey?: string;
};

const FormBuilderRatingScaleNumericInput = ({
	id,
	value,
	onChange,
	precision,
	placeholder,
}: {
	id: string;
	value: number | "";
	onChange: (value: number | "") => void;
	precision: number;
	placeholder: string;
}) => {
	return (
		<InputNumeric
			id={id}
			value={value === "" ? null : value}
			onChange={(value) => onChange(value === null ? "" : value)}
			precision={precision}
			placeholder={placeholder}
			allowEmpty
		/>
	);
};

export const RatingScaleRow = ({ index, field, fieldKey = "choices" }: RatingScaleRowProps) => {
	const { data: settings } = useCompanySettings();
	const { attributes, listeners, setNodeRef, transform } = useSortable({ id: `${index}` });

	const style = {
		transform: CSS.Translate.toString(transform),
	};

	const decimalPlaces = settings?.decimal_places || 0;

	return (
		<div
			id="rating-scale-row"
			ref={setNodeRef}
			style={style}
			{...attributes}
			className="flex items-start gap-2"
			data-testid="rating-scale-row"
		>
			<div
				{...listeners}
				className="flex w-6 flex-shrink-0 cursor-grab items-center justify-center border-r pt-2"
			>
				<Icon
					color="neutral-7"
					dangerouslySetClassName="text-neutral-7 hover:text-neutral-9 active:cursor-grabbing"
					icon={faGripLines}
				/>
			</div>
			<div className="w-[88px]">
				<FormField<number | "">
					id={`${fieldKey}[${index}].value`}
					input={(field) => {
						return (
							<FormBuilderRatingScaleNumericInput
								id={`${fieldKey}[${index}].value`}
								value={field.value}
								onChange={field.onChange}
								precision={decimalPlaces}
								placeholder="Value"
							/>
						);
					}}
				/>
			</div>
			<div className="flex-grow">
				<FormInputText id={`${fieldKey}[${index}].name`} placeholder="Name" />
			</div>
			<div className="flex-grow">
				<FormInputText id={`${fieldKey}[${index}].description`} placeholder="Description" />
			</div>
			{field?.value?.length > 1 && (
				<div className="flexitems-center justify-center">
					<IconButton
						data-testid="delete-option"
						icon={faXmarkCircle}
						color="gray"
						onClick={() => field.removeValue(index)}
						aria-label="Delete option"
						style={{ padding: 0, width: 20 }}
						testId="delete-option"
					/>
				</div>
			)}
		</div>
	);
};

export const RatingScaleEntries = ({
	field,
	index,
	fieldKey,
}: {
	field: ArrayFieldState<QuestionOption>;
	index: number;
	fieldKey: string;
}) => {
	const [ref] = useAutoAnimate();
	const containerRef = useRef<HTMLDivElement>(null);

	const MAX_CHOICES = 20;

	useKeyPress({
		key: "Enter",
		onPress: (e) => {
			e.preventDefault();
			e.stopPropagation();
			//get add button and click it
			const addButton = document.getElementById(`add-option-${index}`);
			if (addButton) {
				addButton.click();
			}
		},
		containerRef: containerRef as React.RefObject<HTMLElement>,
	});

	const { value, pushValue } = field;

	if (!value || value?.length <= 0) {
		return null;
	}

	return (
		<div ref={containerRef}>
			<DragAndDrop
				itemsContainerRef={ref}
				restrictToParentElement
				restrictToVerticalAxis
				itemsContainerClassName="flex flex-col gap-2"
				handleOnDragEnd={(event) => {
					const { active, over } = event;

					if (active.id !== over?.id) {
						const oldIndex = Number(active.id);
						const newIndex = Number(over?.id);
						field.shiftValue(oldIndex, newIndex);
					}
				}}
				itemIds={value?.map((choice, index) => index.toString())}
				draggableNodeItems={value?.map((choice, listIndex) => (
					<RatingScaleRow key={`${listIndex}`} index={listIndex} field={field} fieldKey={fieldKey} />
				))}
			/>

			{value?.length >= MAX_CHOICES && (
				<div className="flex items-center gap-2 pt-2">
					<Icon size={16} color="red-6" icon={faExclamationCircle} />
					<Text size="caption" weight="semi-bold" color="red-6">
						You have reached the maximum number of options
					</Text>
				</div>
			)}
			<Spacer height={2} />
			<div className="flex w-full justify-end">
				<ButtonStandard
					id={`add-option-${index}`}
					iconLeft={faPlus}
					disabled={field.value?.length >= MAX_CHOICES}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						if (field.value?.length >= MAX_CHOICES) {
							return;
						}
						pushValue({ value: "", name: "", description: "" });
						//wait 10 ms and then focus on the last input to wait for render
						setTimeout(() => {
							const lastInput = document.getElementById(`${fieldKey}[${field.value?.length}].value`);
							if (lastInput) {
								lastInput.focus();
							}
						}, 10);
					}}
				>
					Add Option
				</ButtonStandard>
			</div>
		</div>
	);
};

export const LegacyRatingScaleSection = ({ index, fieldKey = "choices" }: { index: number; fieldKey?: string }) => {
	return (
		<div id="rating-scale-section">
			<h6 className="mb-4 text-paragraph-3 font-semi-bold">Rating Scale Options</h6>
			<FormList<QuestionOption>
				id={fieldKey}
				input={(field) => {
					return <RatingScaleEntries field={field} index={index} fieldKey={fieldKey} />;
				}}
			/>
		</div>
	);
};

const LegacyRatingScaleEdit = ({ index }: EditQuestionBlockProps) => {
	return <LegacyRatingScaleSection index={index} />;
};

export default LegacyRatingScaleEdit;
